import React, { ChangeEvent, useState, useEffect } from 'react';
import Modal from './Modal';
import { ModalProps, ModalSize } from './Modal.types';
import {
  Button,
  ButtonSize,
  ButtonType,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  IconName,
} from '@hills-design-system/components';
import { PetParentInfo, PetInfo, ValidationError } from '../../types/petParent';
import DropdownInput, { DropdownInputOption } from '../DropdownInput/DropdownInput';
import InputText from '../InputText/InputText';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { ParsedUploadData } from './SpreadsheetUploadModal';
import {
  convertUTCToLocalDate,
  dateToDateOnlyStringLocal,
  normalizeCalendarDateToDateOnlyString,
  parseDateOnlyStringToLocalDate,
} from '../../utils/dateUtils';
import {
  isValidCanadianPostalCode,
  normalizePostalCode,
} from '../../utils/postalCodeValidation';

export interface ManualEntryModalProps extends Omit<ModalProps, 'children'> {
  /**
   * Report timeframe (e.g. "November 2025")
   */
  reportTimeframe?: string;
  /**
   * Function to call when the form is submitted
   */
  onSubmit: (petParentInfo: PetParentInfo, petInfos: PetInfo[]) => void;
  /**
   * Initial pet parent info for editing, or undefined for new entry
   */
  initialPetParentInfo?: PetParentInfo;
  /**
   * Initial pets array for editing, or undefined for new entry
   */
  initialPetInfos?: PetInfo[];
  /**
   * Whether we're editing an existing entry (true) or adding a new one (false)
   */
  isEditing?: boolean;
  /**
   * Validation errors to display for specific fields
   */
  validationErrors?: ValidationError[];
  /**
   * Whether this modal was opened from ReviewPetParentModal
   */
  isFromReviewModal?: boolean;
  /**
   * Function to call when closing to return to ReviewPetParentModal
   */
  onCloseToReview?: () => void;
  /**
   * Function to call when deleting the current entry from spreadsheet upload
   */
  onDelete?: (index: number) => void;
  parsedUploadData: ParsedUploadData[] | undefined;
  editIndex?: number | null;
  /**
   * Whether this is for a Canadian shelter
   */
  isCanadian?: boolean;
}

// Add this above the ManualEntryModal component
interface AdoptionLocationOptionProps {
  option: DropdownInputOption;
  selected: boolean;
}

const AdoptionLocationOption: React.FC<AdoptionLocationOptionProps> = ({ option, selected }) => (
  <div className="flex flex-row items-center gap-100">
    <span className={`font-semibold text-gray-1100 ${selected ? 'text-primary' : ''}`}>
      {option.label}
    </span>
    {option.description && (
      <span className="text-detail-80 text-gray-700 mt-1">{option.description}</span>
    )}
  </div>
);

/**
 * Modal for manually entering pet parent information
 */
const ManualEntryModal: React.FC<ManualEntryModalProps> = ({
  title,
  eyebrow,
  secondaryEyebrow,
  isOpen,
  onClose,
  reportTimeframe,
  onSubmit,
  size = ModalSize.Large,
  showCloseButton = true,
  className = '',
  initialPetParentInfo,
  initialPetInfos,
  isEditing = false,
  validationErrors = [],
  isFromReviewModal = false,
  onCloseToReview,
  onDelete,
  parsedUploadData,
  editIndex,
  isCanadian = false,
}) => {
  // Empty data for new entries
  const emptyPetParentInfo: PetParentInfo = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    province: '',
    preferredLanguage: '',
  };
  const emptyPetInfo: PetInfo = {
    species: '',
    name: '',
    birthdate: '',
    adoptionDate: '',
    adoptionLocation: '',
    retailPartner: '',
  };

  // State
  const [petParentInfo, setPetParentInfo] = useState<PetParentInfo>(emptyPetParentInfo);
  const [petInfos, setPetInfos] = useState<PetInfo[]>([emptyPetInfo]);
  const [internalValidationErrors, setInternalValidationErrors] = useState<ValidationError[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const { t } = useTranslation();

  const normalizePetDateValue = (value: string | null | undefined): string =>
    normalizeCalendarDateToDateOnlyString(value) ?? '';

  const normalizePetInfosForForm = (pets: PetInfo[] | undefined): PetInfo[] => {
    if (!pets || pets.length === 0) return [emptyPetInfo];

    return pets.map((pet) => ({
      ...pet,
      birthdate: normalizePetDateValue(pet.birthdate),
      adoptionDate: normalizePetDateValue(pet.adoptionDate),
    }));
  };

  const parseCalendarDateForValidation = (value: string | null | undefined): Date | null => {
    if (!value) return null;

    const fromDateOnly = parseDateOnlyStringToLocalDate(value);
    if (fromDateOnly) return fromDateOnly;

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Helper function to get validation error for a specific field
  const getFieldError = (fieldName: string) => {
    // Handle pet-specific field names (e.g., "petName", "petSpecies")
    const petFieldMap: { [key: string]: string } = {
      name: 'petName',
      species: 'petSpecies',
      adoptionDate: 'adoptionDate',
      adoptionLocation: 'adoptionLocation',
      retailPartner: 'retailPartner',
    };

    const actualFieldName = petFieldMap[fieldName] || fieldName;

    // Use internal validation errors for form validation, external for spreadsheet errors
    const errorsToCheck =
      internalValidationErrors.length > 0 ? internalValidationErrors : validationErrors;

    // Return the first matching error for the field
    return errorsToCheck.find((error) => error.field === actualFieldName)?.message;
  };

  // Set default title and eyebrow using translations
  const errorsForTitle =
    internalValidationErrors.length > 0 ? internalValidationErrors : validationErrors;
  const hasErrors = errorsForTitle && errorsForTitle.length > 0;
  let computedModalTitle = '';
  if (isEditing) {
    if (hasErrors) {
      computedModalTitle = `${t(TranslationKeyEnum.EditPetParent)} - ${t(TranslationKeyEnum.ErrorsToFix, errorsForTitle.length)}`;
    } else {
      computedModalTitle = t(TranslationKeyEnum.EditPetParent);
    }
  } else {
    computedModalTitle = t(TranslationKeyEnum.AddNewPetParent);
  }
  const modalTitle = title ?? computedModalTitle;
  const modalEyebrow = eyebrow ?? t(TranslationKeyEnum.PetParentReport);

  // On open, set data correctly based on mode (edit vs add)
  useEffect(() => {
    if (isOpen) {
      // Clear internal validation errors when modal opens
      setInternalValidationErrors([]);
      // Reset change tracking when modal opens
      setHasChanges(false);

      if (isEditing && initialPetParentInfo) {
        // Editing mode - use passed initial data
        setPetParentInfo(initialPetParentInfo);

        // Use initialPetInfos if provided, otherwise start with empty pet
        const normalizedPetInfos = normalizePetInfosForForm(initialPetInfos);
        setPetInfos(normalizedPetInfos);

        const errors = validateForm(initialPetParentInfo, normalizedPetInfos);
        if (errors.length > 0) {
          setInternalValidationErrors(errors);
        }
      } else {
        // Adding without random data - use empty data
        setPetParentInfo(emptyPetParentInfo);
        setPetInfos([emptyPetInfo]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditing, initialPetParentInfo, initialPetInfos]);

  // Scroll to first error field when modal opens with errors
  useEffect(() => {
    if (isOpen && isEditing && validationErrors && validationErrors.length > 0) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        const firstError = validationErrors[0];
        const fieldName = firstError.field;

        // Map backend field names to frontend field IDs
        const fieldIdMap: { [key: string]: string } = {
          firstName: 'firstName',
          lastName: 'lastName',
          email: 'email',
          phone: 'phone',
          address: 'address',
          city: 'city',
          state: 'state',
          zip: 'zip',
          petName: 'petName-0', // First pet
          petSpecies: 'species-0', // First pet
          adoptionDate: 'adoptionDate-0', // First pet
          adoptionLocation: 'adoptionLocation-0', // First pet
          retailPartner: 'retailPartner-0', // First pet
        };

        const elementId = fieldIdMap[fieldName];
        if (elementId) {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }
      }, 300);
    }
  }, [isOpen, isEditing, validationErrors]);

  const speciesOptions: DropdownInputOption[] = [
    { id: 'dog', label: t(TranslationKeyEnum.Dog) },
    { id: 'cat', label: t(TranslationKeyEnum.Cat) },
  ];

  const stateOptions: DropdownInputOption[] = [
    { id: 'AL', label: 'Alabama (AL)' },
    { id: 'AK', label: 'Alaska (AK)' },
    { id: 'AZ', label: 'Arizona (AZ)' },
    { id: 'AR', label: 'Arkansas (AR)' },
    { id: 'CA', label: 'California (CA)' },
    { id: 'CO', label: 'Colorado (CO)' },
    { id: 'CT', label: 'Connecticut (CT)' },
    { id: 'DE', label: 'Delaware (DE)' },
    { id: 'DC', label: 'District of Columbia (DC)' },
    { id: 'FL', label: 'Florida (FL)' },
    { id: 'GA', label: 'Georgia (GA)' },
    { id: 'HI', label: 'Hawaii (HI)' },
    { id: 'ID', label: 'Idaho (ID)' },
    { id: 'IL', label: 'Illinois (IL)' },
    { id: 'IN', label: 'Indiana (IN)' },
    { id: 'IA', label: 'Iowa (IA)' },
    { id: 'KS', label: 'Kansas (KS)' },
    { id: 'KY', label: 'Kentucky (KY)' },
    { id: 'LA', label: 'Louisiana (LA)' },
    { id: 'ME', label: 'Maine (ME)' },
    { id: 'MD', label: 'Maryland (MD)' },
    { id: 'MA', label: 'Massachusetts (MA)' },
    { id: 'MI', label: 'Michigan (MI)' },
    { id: 'MN', label: 'Minnesota (MN)' },
    { id: 'MS', label: 'Mississippi (MS)' },
    { id: 'MO', label: 'Missouri (MO)' },
    { id: 'MT', label: 'Montana (MT)' },
    { id: 'NE', label: 'Nebraska (NE)' },
    { id: 'NV', label: 'Nevada (NV)' },
    { id: 'NH', label: 'New Hampshire (NH)' },
    { id: 'NJ', label: 'New Jersey (NJ)' },
    { id: 'NM', label: 'New Mexico (NM)' },
    { id: 'NY', label: 'New York (NY)' },
    { id: 'NC', label: 'North Carolina (NC)' },
    { id: 'ND', label: 'North Dakota (ND)' },
    { id: 'OH', label: 'Ohio (OH)' },
    { id: 'OK', label: 'Oklahoma (OK)' },
    { id: 'OR', label: 'Oregon (OR)' },
    { id: 'PA', label: 'Pennsylvania (PA)' },
    { id: 'RI', label: 'Rhode Island (RI)' },
    { id: 'SC', label: 'South Carolina (SC)' },
    { id: 'SD', label: 'South Dakota (SD)' },
    { id: 'TN', label: 'Tennessee (TN)' },
    { id: 'TX', label: 'Texas (TX)' },
    { id: 'UT', label: 'Utah (UT)' },
    { id: 'VT', label: 'Vermont (VT)' },
    { id: 'VA', label: 'Virginia (VA)' },
    { id: 'WA', label: 'Washington (WA)' },
    { id: 'WV', label: 'West Virginia (WV)' },
    { id: 'WI', label: 'Wisconsin (WI)' },
    { id: 'WY', label: 'Wyoming (WY)' },
  ];

  const canadianProvinceOptions: DropdownInputOption[] = [
    { id: 'AB', label: t(TranslationKeyEnum.Alberta) },
    { id: 'BC', label: t(TranslationKeyEnum.BritishColumbia) },
    { id: 'MB', label: t(TranslationKeyEnum.Manitoba) },
    { id: 'NB', label: t(TranslationKeyEnum.NewBrunswick) },
    { id: 'NL', label: t(TranslationKeyEnum.Newfoundland) },
    { id: 'NS', label: t(TranslationKeyEnum.NovaScotia) },
    { id: 'ON', label: t(TranslationKeyEnum.Ontario) },
    { id: 'PE', label: t(TranslationKeyEnum.PrinceEdwardIsland) },
    { id: 'QC', label: t(TranslationKeyEnum.Quebec) },
    { id: 'SK', label: t(TranslationKeyEnum.Saskatchewan) },
    { id: 'NT', label: t(TranslationKeyEnum.NorthwestTerritories) },
    { id: 'NU', label: t(TranslationKeyEnum.Nunavut) },
    { id: 'YT', label: t(TranslationKeyEnum.Yukon) },
  ];

  const preferredLanguageOptions: DropdownInputOption[] = [
    { id: 'english', label: t(TranslationKeyEnum.English) },
    { id: 'french', label: t(TranslationKeyEnum.French) },
  ];

  const adoptionLocationOptions: DropdownInputOption[] = [
    {
      id: 'shelter',
      label: t(TranslationKeyEnum.ShelterAdoption),
      description: t(TranslationKeyEnum.ShelterAdoptionDescription),
    },
    {
      id: 'event',
      label: t(TranslationKeyEnum.AdoptionEvent),
      description: t(TranslationKeyEnum.AdoptionEventDescription),
    },
    {
      id: 'retail',
      label: t(TranslationKeyEnum.RetailPartnerAdoption),
      description: t(TranslationKeyEnum.RetailPartnerAdoptionDescription),
    },
  ];

  const handlePetParentChange = (field: keyof PetParentInfo, value: string) => {
    setPetParentInfo((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handlePetInfoChange = (
    field: keyof Omit<PetInfo, 'birthdate' | 'adoptionDate'>,
    value: string,
    index: number
  ) => {
    setPetInfos((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setHasChanges(true);
  };

  const handlePetInfoDateChange = (
    field: 'birthdate' | 'adoptionDate',
    value: Date | null,
    index: number
  ) => {
    const normalizedDate = dateToDateOnlyStringLocal(value) ?? '';
    setPetInfos((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: normalizedDate };
      return updated;
    });
    setHasChanges(true);
  };

  const handleSubmit = () => {
    const errors = validateForm();
    setInternalValidationErrors(errors);

    if (errors?.length === 0) {
      // Only call parent's onSubmit when validation passes
      onSubmit(petParentInfo, petInfos);
      onClose();
      // Reset form to empty state
      setPetParentInfo(emptyPetParentInfo);
      setPetInfos([emptyPetInfo]);
      setInternalValidationErrors([]);
    } else {
      // Optional: Scroll to the first error
      setTimeout(() => {
        const firstErrorField = errors[0].field;
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleDelete = () => {
    if (onDelete && editIndex !== null && editIndex !== undefined) {
      onDelete(editIndex);
      // Return to review modal instead of just closing
      if (onCloseToReview) {
        onCloseToReview();
      } else {
        onClose();
      }
    }
  };

  const showErrorMessage = () => {
    // Use internal validation errors for form validation, external for spreadsheet errors
    const errorsToShow =
      internalValidationErrors.length > 0 ? internalValidationErrors : validationErrors;
    if (errorsToShow.length === 0) return '';
    if (errorsToShow.length === 1) {
      return t(TranslationKeyEnum.ResolveErrorBeforeSubmitting);
    }
    return t(TranslationKeyEnum.ResolveMultipleErrorsBeforeSubmitting, errorsToShow.length);
  };

  const handleAddAnotherPet = () => {
    setPetInfos((prev) => [...prev, emptyPetInfo]);
  };

  const handleRemovePet = (index: number) => {
    setPetInfos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    if (isFromReviewModal && onCloseToReview) {
      onCloseToReview();
    } else if (showDiscardConfirmation) {
      cancelDiscard(); // If already showing confirmation, just cancel it
    } else if (!showDiscardConfirmation && hasChanges) {
      setShowDiscardConfirmation(true); // Show confirmation dialog if changes exist
    } else {
      onClose(); // Close modal directly if no changes
    }
  };

  const validateEmailFormat = (email: string): boolean => {
    // More robust email regex that catches common invalid patterns
    const emailRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
    return emailRegex.test(email);
  };

  const validateNameFormat = (name: string): boolean => {
    // Allow unicode letters (e.g. French accents), spaces, hyphens, apostrophes
    const nameRegex = /^[\p{L}\s'-]+$/u;
    return nameRegex.test(name);
  };

  const validatePetNameFormat = (name: string): boolean => {
    // Allow numbers, unicode letters, spaces, hyphens, apostrophes
    const nameRegex = /^[\p{L}0-9\s'-]+$/u;
    return nameRegex.test(name);
  };

  const validateAddressFormat = (address: string): boolean => {
    // Allow unicode letters, numbers, spaces, hyphens, apostrophes, periods, commas
    const addressRegex = /^[\p{L}0-9\s'-.#,]+$/u;
    return addressRegex.test(address);
  };

  const validateCityFormat = (city: string): boolean => {
    // Allow unicode letters, spaces, hyphens, apostrophes (e.g. Montréal, St. Mary's)
    const cityRegex = /^[\p{L}\s'.-]+$/u;
    return cityRegex.test(city);
  };

  const validateStateFormat = (state: string): boolean => {
    // Allow letters, spaces, hyphens (for state names)
    const stateRegex = /^[a-zA-Z\s-]+$/;
    return stateRegex.test(state);
  };

  const validatePhoneFormat = (phone: string): boolean => {
    const phoneRegex = /^(\+1\s?)?\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/;
    return phoneRegex.test(phone);
  };

  const validateZipFormat = (zip: string): boolean => {
    const zipRegex = /^\d{5}(?:-\d{0,4})?$/;
    return zipRegex.test(zip) && (zip.length === 5 || zip.length === 10);
  };

  const validateCanadianPostalCodeFormat = (postalCode: string): boolean => {
    return isValidCanadianPostalCode(postalCode);
  };

  const validatePetInfo = (pet: PetInfo, index: number): ValidationError[] => {
    const petErrors: ValidationError[] = [];

    if (!pet.species)
      petErrors.push({
        field: `species-${index}`,
        message: t(TranslationKeyEnum.RequiredField),
        row: index,
      });
    if (!pet.name.trim())
      petErrors.push({
        field: `petName-${index}`,
        message: t(TranslationKeyEnum.RequiredField),
        row: index,
      });
    else if (!validatePetNameFormat(pet.name.trim())) {
      petErrors.push({
        field: `petName-${index}`,
        message: t(TranslationKeyEnum.InvalidName),
        row: index,
      });
    }
    if (!pet.adoptionDate) {
      petErrors.push({
        field: `adoptionDate-${index}`,
        message: t(TranslationKeyEnum.RequiredField),
        row: index,
      });
    } else {
      const adoptionDate = parseCalendarDateForValidation(pet.adoptionDate);
      const currentDate = new Date();

      // Check if adoptionDate is not in the future
      if (adoptionDate && adoptionDate > currentDate) {
        petErrors.push({
          field: `adoptionDate-${index}`,
          message: t(TranslationKeyEnum.AdoptionDateCannotBeFutureDate),
          row: index,
        });
      }
    }
    if (!pet.adoptionLocation)
      petErrors.push({
        field: `adoptionLocation-${index}`,
        message: t(TranslationKeyEnum.RequiredField),
        row: index,
      });

    // Retail partner if not empty should be validated
    if (pet.retailPartner?.trim() && !validateNameFormat(pet.retailPartner.trim()))
      petErrors.push({
        field: `retailPartner-${index}`,
        message: t(TranslationKeyEnum.InvalidName),
        row: index,
      });

    // Validate birthdate if provided
    if (pet.birthdate) {
      const birthDate = parseCalendarDateForValidation(pet.birthdate);
      const currentDate = new Date();

      // Check if birthdate is not in the future
      if (birthDate && birthDate > currentDate) {
        petErrors.push({
          field: `birthdate-${index}`,
          message: t(TranslationKeyEnum.BirthDateCannotBeFutureDate),
          row: index,
        });
      }

      // Check if birthdate is not after adoption date (if adoption date is provided)
      if (pet.adoptionDate) {
        const adoptionDate = parseCalendarDateForValidation(pet.adoptionDate);
        if (birthDate && adoptionDate && birthDate > adoptionDate) {
          petErrors.push({
            field: `birthdate-${index}`,
            message: t(TranslationKeyEnum.BirthDateCannotBeAfterAdoptionDate),
            row: index,
          });
        }
      }
    }

    return petErrors;
  };

  const validateForm = (petParentData = petParentInfo, petData = petInfos) => {
    const errors: ValidationError[] = [];

    // Check pet parent info
    if (!petParentData.firstName.trim())
      errors.push({ field: 'firstName', message: t(TranslationKeyEnum.RequiredField), row: 0 });
    else if (!validateNameFormat(petParentData.firstName.trim())) {
      errors.push({
        field: 'firstName',
        message: t(TranslationKeyEnum.InvalidName),
        row: 0,
      });
    }

    if (!petParentData.lastName.trim())
      errors.push({ field: 'lastName', message: t(TranslationKeyEnum.RequiredField), row: 0 });
    else if (!validateNameFormat(petParentData.lastName.trim())) {
      errors.push({
        field: 'lastName',
        message: t(TranslationKeyEnum.InvalidName),
        row: 0,
      });
    }

    if (!petParentData.email.trim())
      errors.push({ field: 'email', message: t(TranslationKeyEnum.RequiredField), row: 0 });
    else if (!validateEmailFormat(petParentData.email)) {
      errors.push({ field: 'email', message: t(TranslationKeyEnum.InvalidEmail), row: 0 });
    }

    if (petParentData.phone && !validatePhoneFormat(petParentData.phone)) {
      errors.push({ field: 'phone', message: t(TranslationKeyEnum.InvalidPhone), row: 0 });
    }

    if (!petParentData.address.trim())
      errors.push({ field: 'address', message: t(TranslationKeyEnum.RequiredField), row: 0 });
    else if (!validateAddressFormat(petParentData.address.trim())) {
      errors.push({
        field: 'address',
        message: t(TranslationKeyEnum.InvalidAddress),
        row: 0,
      });
    }
    if (!petParentData.city.trim())
      errors.push({ field: 'city', message: t(TranslationKeyEnum.RequiredField), row: 0 });
    else if (!validateCityFormat(petParentData.city.trim())) {
      errors.push({
        field: 'city',
        message: t(TranslationKeyEnum.InvalidCity),
        row: 0,
      });
    }

    // Canadian-specific validation
    if (isCanadian) {
      if (!petParentData.province?.trim())
        errors.push({ field: 'province', message: t(TranslationKeyEnum.RequiredField), row: 0 });

      if (!petParentData.preferredLanguage?.trim())
        errors.push({ field: 'preferredLanguage', message: t(TranslationKeyEnum.RequiredField), row: 0 });

      if (!petParentData.zip.trim())
        errors.push({ field: 'postalCode', message: t(TranslationKeyEnum.RequiredField), row: 0 });
      else if (!validateCanadianPostalCodeFormat(petParentData.zip.trim())) {
        errors.push({
          field: 'postalCode',
          message: t(TranslationKeyEnum.InvalidPostalCode),
          row: 0,
        });
      }
    } else {
      // US-specific validation
      if (!petParentData.state.trim())
        errors.push({ field: 'state', message: t(TranslationKeyEnum.RequiredField), row: 0 });
      else if (!validateStateFormat(petParentData.state.trim())) {
        errors.push({
          field: 'state',
          message: t(TranslationKeyEnum.InvalidState),
          row: 0,
        });
      }

      if (!petParentData.zip.trim())
        errors.push({ field: 'zip', message: t(TranslationKeyEnum.RequiredField), row: 0 });
      else if (!validateZipFormat(petParentData.zip)) {
        errors.push({ field: 'zip', message: t(TranslationKeyEnum.InvalidZipCode), row: 0 });
      }
    }

    // Check each pet's info
    petData.forEach((pet, index) => {
      const petErrors = validatePetInfo(pet, index);
      errors.push(...petErrors);
    });

    // Check for pet name and adoption date duplicates
    const petUniquenessMap = new Map<string, number[]>(); // Maps "PET_NAME|ADOPTION_DATE" to an array of indices

    petData.forEach((pet, index) => {
      // Only check if both the name AND adoption date are present
      if (pet.name.trim() && pet.adoptionDate) {
        // Combine the standardized name and date with a separator (e.g., '|')
        const key = `${pet.name.trim().toUpperCase()}|${pet.adoptionDate}`;
        if (petUniquenessMap.has(key)) {
          petUniquenessMap.get(key)!.push(index);
        } else {
          petUniquenessMap.set(key, [index]);
        }
      }
    });

    // Add error for any pet involved in a duplicate name group
    petUniquenessMap.forEach((indices) => {
      if (indices.length > 1) {
        // Loop starts from index 1 (the SECOND pet in the list)
        for (let i = 1; i < indices.length; i++) {
          const index = indices[i];
          errors.push({
            field: `petName-${index}`,
            message: t(TranslationKeyEnum.DuplicatePetName),
            row: index,
          });
        }
      }
    });
    if (isEditing) {
      validateEmailAcrossAllPetParentData(errors, petParentData.email);
    }
    return errors;
  };

  const validateEmailAcrossAllPetParentData = (errors: ValidationError[], email: string) => {
    if (parsedUploadData) {
      const duplicateEmail = parsedUploadData.filter(
        (data, index) =>
          index !== editIndex &&
          data.petParentInfo.email.trim().toLowerCase() === email.trim().toLowerCase()
      );
      if (duplicateEmail.length > 0) {
        errors.push({
          field: 'email',
          message: t(TranslationKeyEnum.EmailAlreadyExists),
          row: 0,
        });
      }
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirmation(false);
    setHasChanges(false);
    onClose();
  };
  const cancelDiscard = () => {
    setShowDiscardConfirmation(false);
  };

  return (
    <Modal
      title={!showDiscardConfirmation ? modalTitle : ''}
      eyebrow={!showDiscardConfirmation ? modalEyebrow : ''}
      secondaryEyebrow={!showDiscardConfirmation ? (secondaryEyebrow ?? reportTimeframe) : ''}
      isOpen={isOpen}
      onClose={handleClose}
      size={size}
      showCloseButton={showCloseButton}
      className={className}
      errorMessage={!showDiscardConfirmation ? showErrorMessage() : ''}
    >
      {!showDiscardConfirmation && (
        <div className="flex flex-col h-full relative">
          <div className="flex-1 overflow-y-auto pb-500">
            <div className="flex flex-col gap-200 w-full">
              {/* Pet Parent Information Section */}
              <div className="flex flex-col gap-6 w-full">
                <div className="flex flex-col gap-2">
                  <h3 className="font-montserrat font-bold text-heading-125 leading-heading text-gray-1100 ">
                    {t(TranslationKeyEnum.PetParentInformation)}
                  </h3>
                </div>
                <div className="flex flex-col gap-4 w-full">
                {isCanadian && (
                    <div className="flex w-full">
                      <DropdownInput
                        id="preferredLanguage"
                        label={t(TranslationKeyEnum.PreferredLanguage)}
                        options={preferredLanguageOptions}
                        value={petParentInfo.preferredLanguage || ''}
                        onChange={(id) => handlePetParentChange('preferredLanguage', id)}
                        required
                        className="w-full"
                        error={getFieldError('preferredLanguage')}
                      />
                    </div>
                  )}
                  <div className="flex gap-100 w-full">
                    <InputText
                      containerClassName="flex-1"
                      label={t(TranslationKeyEnum.FirstName)}
                      id="firstName"
                      type="text"
                      value={petParentInfo.firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handlePetParentChange('firstName', e.target.value)
                      }
                      required
                      error={getFieldError('firstName')}
                    />
                    <InputText
                      containerClassName="flex-1"
                      label={t(TranslationKeyEnum.LastName)}
                      id="lastName"
                      type="text"
                      value={petParentInfo.lastName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handlePetParentChange('lastName', e.target.value)
                      }
                      required
                      error={getFieldError('lastName')}
                    />
                  </div>
                  <div className="flex gap-100 w-full">
                    <InputText
                      containerClassName="flex-1"
                      label={t(TranslationKeyEnum.EmailAddress)}
                      id="email"
                      type="text"
                      value={petParentInfo.email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handlePetParentChange('email', e.target.value)
                      }
                      required
                      error={getFieldError('email')}
                    />
                    <InputText
                      containerClassName="flex-1"
                      label={t(TranslationKeyEnum.PhoneNumber)}
                      id="phone"
                      type="text"
                      value={petParentInfo.phone}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handlePetParentChange('phone', e.target.value)
                      }
                      error={getFieldError('phone')}
                    />
                  </div>
                  <div className="flex w-full">
                    <InputText
                      containerClassName="flex-1"
                      label={t(TranslationKeyEnum.Address)}
                      id="address"
                      type="text"
                      value={petParentInfo.address}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handlePetParentChange('address', e.target.value)
                      }
                      required
                      error={getFieldError('address')}
                      maxLength={200}
                    />
                  </div>
                  <div className="flex gap-100 w-full">
                    <InputText
                      containerClassName="flex-1"
                      label={t(TranslationKeyEnum.City)}
                      id="city"
                      type="text"
                      value={petParentInfo.city}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handlePetParentChange('city', e.target.value)
                      }
                      required
                      error={getFieldError('city')}
                    />
                    {isCanadian ? (
                      <>
                        <DropdownInput
                          id="province"
                          label={t(TranslationKeyEnum.Province)}
                          options={canadianProvinceOptions}
                          value={petParentInfo.province || ''}
                          onChange={(id) => handlePetParentChange('province', id)}
                          required
                          className="min-w-[240px]"
                          canSearch={true}
                          error={getFieldError('province')}
                        />
                        <InputText
                          containerClassName="flex-1"
                          label={t(TranslationKeyEnum.PostalCode)}
                          id="postalCode"
                          type="text"
                          placeholder="A1A 1A1"
                          value={petParentInfo.zip}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handlePetParentChange(
                              'zip',
                              normalizePostalCode(e.target.value)
                            )
                          }
                          required
                          maxLength={6}
                          error={getFieldError('postalCode')}
                        />
                      </>
                    ) : (
                      <DropdownInput
                        id="state"
                        label={t(TranslationKeyEnum.State)}
                        options={stateOptions}
                        value={petParentInfo.state}
                        onChange={(id) => handlePetParentChange('state', id)}
                        required
                        className="min-w-[240px]"
                        canSearch={true}
                        error={getFieldError('state')}
                      />
                    )}
                  </div>
                  {!isCanadian && (
                    <div className="flex w-full">
                      <InputText
                        containerClassName="flex-1"
                        label={t(TranslationKeyEnum.ZipCode)}
                        id="zip"
                        type="text"
                        value={petParentInfo.zip}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handlePetParentChange('zip', e.target.value.replace(/\s/g, ''))
                        }
                        required
                        maxLength={10}
                        error={getFieldError('zip')}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Pet Information Section */}
              <div className="flex flex-col gap-6 w-full">
                <div className="flex flex-col gap-2">
                  <h3 className="font-montserrat font-bold text-heading-125 leading-heading text-gray-1100">
                    {t(TranslationKeyEnum.PetInformation)}
                  </h3>
                </div>
                {petInfos.map((pet, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-4 w-full border-b pb-4 mb-4 relative"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-bold">
                        {t(TranslationKeyEnum.PetNumberInformation, index + 1)}
                      </div>
                      {petInfos.length > 1 && (
                        <IconButton
                          icon={IconName.Trash}
                          ariaLabel={t(TranslationKeyEnum.RemovePet)}
                          size={IconButtonSize.Small}
                          variant={IconButtonVariant.Destructive}
                          onClick={() => handleRemovePet(index)}
                          className="ml-2"
                        />
                      )}
                    </div>
                    <div className="flex w-full">
                      <div className="flex-1">
                        <DropdownInput
                          id={`species-${index}`}
                          label={t(TranslationKeyEnum.Species)}
                          options={speciesOptions}
                          value={pet.species}
                          onChange={(id) => handlePetInfoChange('species', id, index)}
                          required
                          className="w-full"
                          error={getFieldError(`species-${index}`)}
                        />
                      </div>
                    </div>
                    <div className="flex gap-100 w-full">
                      <InputText
                        containerClassName="flex-1"
                        label={t(TranslationKeyEnum.PetName)}
                        id={`petName-${index}`}
                        type="text"
                        value={pet.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handlePetInfoChange('name', e.target.value, index)
                        }
                        required
                        error={getFieldError(`petName-${index}`)}
                      />
                      <div className="flex-1">
                        <InputText
                          containerClassName="h-full"
                          id={`birthdate-${index}`}
                          label={t(TranslationKeyEnum.EstimatedBirthdate)}
                          type="date"
                          maxAllowedDate={new Date()}
                          value={convertUTCToLocalDate(pet.birthdate)}
                          onChange={(date) => handlePetInfoDateChange('birthdate', date, index)}
                          error={getFieldError(`birthdate-${index}`)}
                        />
                      </div>
                    </div>
                    <div className="flex w-full">
                      <div className="flex-1">
                        <InputText
                          containerClassName="h-full"
                          id={`adoptionDate-${index}`}
                          label={t(TranslationKeyEnum.AdoptionDate)}
                          type="date"
                          maxAllowedDate={new Date()}
                          value={convertUTCToLocalDate(pet.adoptionDate)}
                          onChange={(date) => handlePetInfoDateChange('adoptionDate', date, index)}
                          required
                          error={getFieldError(`adoptionDate-${index}`)}
                        />
                      </div>
                    </div>
                    <div className="flex w-full">
                      <div className="flex-1">
                        <DropdownInput
                          id={`adoptionLocation-${index}`}
                          label={t(TranslationKeyEnum.AdoptionLocation)}
                          options={adoptionLocationOptions}
                          value={pet.adoptionLocation}
                          onChange={(id) => handlePetInfoChange('adoptionLocation', id, index)}
                          required
                          className="w-full"
                          renderOption={(option, selected) => (
                            <AdoptionLocationOption option={option} selected={selected} />
                          )}
                          error={getFieldError(`adoptionLocation-${index}`)}
                        />
                      </div>
                    </div>
                    <div className="flex w-full">
                      <InputText
                        containerClassName="flex-1"
                        label={t(TranslationKeyEnum.RetailPartner)}
                        id={`retailPartner-${index}`}
                        type="text"
                        value={pet.retailPartner}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handlePetInfoChange('retailPartner', e.target.value, index)
                        }
                        placeholder={t(TranslationKeyEnum.RetailPartnerPlaceholder)}
                        error={getFieldError(`retailPartner-${index}`)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Another Pet Link */}
              <div className="flex items-center justify-center">
                <Button
                  label={t(TranslationKeyEnum.AddAnotherPetForParent)}
                  type={ButtonType.Tertiary}
                  onClick={handleAddAnotherPet}
                  className="mb-100 z-20 hover:bg-transparent"
                />
              </div>
              {/* Canadian Consent Disclaimer (Informational Only) */}
              {isCanadian && (
                <div className="flex flex-col gap-4 w-full p-4 rounded-lg border border-[#EAEAEA]">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {t(TranslationKeyEnum.CanadianConsentStatement)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Fixed Bottom Bar with Delete and Submit Buttons */}
          <div className="sticky bottom-[-32px] left-0 right-0 -mx-8 z-60 bg-white py-200 px-200">
            <div className="flex justify-end gap-150">
              {isEditing && isFromReviewModal && onDelete && (
                <Button
                  label={t(TranslationKeyEnum.Delete)}
                  type={ButtonType.Secondary}
                  onClick={handleDelete}
                  className="py-100 px-125 h-[52px]"
                />
              )}
              <Button
                label={t(TranslationKeyEnum.ReviewAndSubmit)}
                type={ButtonType.Primary}
                onClick={handleSubmit}
                className="py-100 px-125 h-[52px]"
              />
            </div>
          </div>
        </div>
      )}

      {/* Discard Confirmation Dialog */}
      {showDiscardConfirmation && (
        <div className="flex flex-col gap-60 px-200 pb-500 items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-3 w-[400px]">
            <h3 className="text-heading-150 font-bold text-black mb-0">
              {t(TranslationKeyEnum.DiscardReport)}
            </h3>
            <p className="font-normal text-center text-gray-500 mb-100">
              {t(TranslationKeyEnum.DiscardPetParentReportMessage)}
            </p>
            <div className="flex justify-end gap-100">
              <Button
                label={t(TranslationKeyEnum.GoBack)}
                type={ButtonType.Secondary}
                size={ButtonSize.Small}
                onClick={cancelDiscard}
              />
              <Button
                label={t(TranslationKeyEnum.YesDiscard)}
                type={ButtonType.Primary}
                size={ButtonSize.Small}
                onClick={confirmDiscard}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ManualEntryModal;
